import i18next from 'i18next';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import { ToggleButton } from '@commons/utils/styledLibraryComponents';
import DeepsightFiltersButton from '@commons/DeepsightAnalyticsHeaders/components/DeepsightFiltersButton';

import { Container, FiltersContainer, ToggleContainer } from './styledComponents';

export const DISPLAY_MODE = {
  PERCENT: 'PERCENT',
  EURO: 'EURO',
};

export const VARIANTS_DROPDOWN_ID = 'variants';
export const STORES_DROPDOWN_ID = 'performancesStores';
const CHANNELS_DROPDOWN_ID = 'channels';

const VARIANTS_DROPDOWN_ICON = '/images/inpulse/call-split-black-small.svg';
const STORES_DROPDOWN_ICON = '/images/inpulse/store-black-small.svg';
const CHANNELS_DROPDOWN_ICON = '/images/inpulse/supplier-black-small.svg';

const PerformancesHeader = (props) => {
  const {
    advancedFilters,
    areAdvancedFiltersDisabled,
    clientChannels,
    currency,
    displayMode,
    filters,
    isChannelDropdownDisabled,
    recipeVariants,
    selectedChannels,
    selectedStores,
    selectedVariants,
    setAdvancedFilters,
    setApplyFilters,
    setDisplayMode,
    setFilters,
    setSelectedChannels,
    setSelectedStores,
    setSelectedVariants,
    availableStores,
  } = props;

  const displayModeChoices = useMemo(
    () => [
      {
        key: DISPLAY_MODE.PERCENT,
        name: i18next.t('ADMIN.RECIPES.PERFORMANCES_TOGGLE_RATIO'),
      },
      {
        key: DISPLAY_MODE.EURO,
        name: i18next.t('ADMIN.RECIPES.PERFORMANCES_TOGGLE_COST', {
          currencyCode: currency?.alphabeticCode ?? '',
        }),
      },
    ],
    [currency?.alphabeticCode],
  );

  const selectedDisplayChoice = useMemo(
    () => displayModeChoices.find(({ key }) => key === displayMode),
    [displayModeChoices, displayMode],
  );

  const variantsDropdown = useMemo(
    () => ({
      id: VARIANTS_DROPDOWN_ID,
      icon: VARIANTS_DROPDOWN_ICON,
      list: recipeVariants,
      defaultSelectedItems: recipeVariants,
      selectedItems: selectedVariants,
      setSelectedItems: setSelectedVariants,
      forceStateUpdateTrigger: true,
    }),
    [recipeVariants, selectedVariants, setSelectedVariants],
  );

  // Stores rendered as a customMultipleDropDown to control its position (2nd) in the popup
  const storesDropdown = useMemo(
    () => ({
      id: STORES_DROPDOWN_ID,
      icon: STORES_DROPDOWN_ICON,
      list: availableStores,
      defaultSelectedItems: availableStores,
      selectedItems: selectedStores,
      setSelectedItems: setSelectedStores,
    }),
    [availableStores, selectedStores, setSelectedStores],
  );

  const channelsDropdown = useMemo(
    () => ({
      id: CHANNELS_DROPDOWN_ID,
      icon: CHANNELS_DROPDOWN_ICON,
      list: clientChannels,
      defaultSelectedItems: clientChannels,
      selectedItems: selectedChannels,
      setSelectedItems: setSelectedChannels,
      isDisabled: isChannelDropdownDisabled,
    }),
    [clientChannels, selectedChannels, setSelectedChannels, isChannelDropdownDisabled],
  );

  const handleDisplayModeChange = (choice) => {
    setDisplayMode(choice.key);
  };

  return (
    <Container>
      <FiltersContainer>
        <DeepsightFiltersButton
          advancedFilters={advancedFilters}
          customMultipleDropDowns={[variantsDropdown, storesDropdown, channelsDropdown]}
          filters={filters}
          readOnly={areAdvancedFiltersDisabled}
          setAdvancedFilters={setAdvancedFilters}
          setApplyFilters={setApplyFilters}
          setFilters={setFilters}
          textFilterButton={i18next.t('ADMIN.RECIPES.PERFORMANCES_ADVANCED_FILTERS')}
        />
      </FiltersContainer>
      <ToggleContainer>
        <ToggleButton
          choices={displayModeChoices}
          selectedChoice={selectedDisplayChoice}
          setSelectedChoice={handleDisplayModeChange}
        />
      </ToggleContainer>
    </Container>
  );
};

PerformancesHeader.propTypes = {
  advancedFilters: PropTypes.arrayOf(PropTypes.object),
  areAdvancedFiltersDisabled: PropTypes.bool.isRequired,
  clientChannels: PropTypes.arrayOf(PropTypes.object).isRequired,
  currency: PropTypes.object,
  displayMode: PropTypes.oneOf(Object.values(DISPLAY_MODE)).isRequired,
  filters: PropTypes.object,
  isChannelDropdownDisabled: PropTypes.bool.isRequired,
  recipeVariants: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedChannels: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedStores: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedVariants: PropTypes.arrayOf(PropTypes.object).isRequired,
  setAdvancedFilters: PropTypes.func.isRequired,
  setApplyFilters: PropTypes.func.isRequired,
  setDisplayMode: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  setSelectedChannels: PropTypes.func.isRequired,
  setSelectedStores: PropTypes.func.isRequired,
  setSelectedVariants: PropTypes.func.isRequired,
  availableStores: PropTypes.arrayOf(PropTypes.object).isRequired,
};

PerformancesHeader.defaultProps = {
  advancedFilters: null,
  currency: null,
  filters: null,
};

export default PerformancesHeader;
